import axios, { AxiosInstance, isAxiosError } from 'axios';

import { Identity } from '../components/header/models/identity';
import { isViewAs } from '../viewAs/viewAsSession';

import { healioSpaceAPIURL } from './baseHealioSpaceAPI';

/**
 * HA-445: the API signs the user in and keeps their tokens. The browser holds only an HttpOnly
 * session cookie that no script can read; who the user is comes from GET /auth/session, read once
 * before the app renders (index.tsx) and held here in memory.
 */

/** Sent on every request the API authenticates from the session cookie (CSRF, HA-445 Q5). */
export const HEALIO_REQUEST_HEADER = 'X-Healio-Request';

/** Marks a support view-as tab's requests: the API never signs those in from the cookie (HA-447). */
export const HEALIO_VIEW_AS_HEADER = 'X-Healio-View-As';

export interface SessionDto {
  claims: Record<string, unknown>;
  idleExpiresAt: string;
  absoluteExpiresAt: string;
}

export interface ServerSession {
  claims: Identity;
  /** When the session ends unless refreshed (15 minutes idle), ms since epoch. */
  idleExpiresAt: number;
  /** When the session ends regardless (12 hours after sign-in), ms since epoch. */
  absoluteExpiresAt: number;
  /**
   * When this page last told the API the user is active, ms since epoch. A page load starts the
   * count (the load isn't activity the API has heard about, but it isn't a reason to refresh).
   */
  refreshedAt: number;
}

export type SignInStep =
  | { status: 'signed-in' }
  | {
      status: 'challenge';
      challengeId: string;
      challengeName: 'SOFTWARE_TOKEN_MFA' | 'NEW_PASSWORD_REQUIRED' | string;
    };

/** The API's sign-in failure codes (AuthFailureCodes in healiospace-api-net). */
export type SignInFailureCode =
  | 'INVALID_CREDENTIALS'
  | 'INVALID_CODE'
  | 'CHALLENGE_EXPIRED'
  | 'NOT_CONFIRMED'
  | 'INVALID_PASSWORD'
  | 'TOO_MANY_ATTEMPTS'
  | 'UNSUPPORTED_CHALLENGE'
  | 'UNAVAILABLE';

/** A refused sign-in step; `code` is the API's, `message` is for the user. */
export class SignInError extends Error {
  constructor(
    public readonly code: SignInFailureCode,
    message: string
  ) {
    super(message);
    this.name = 'SignInError';
  }
}

const SIGN_IN_MESSAGES: Record<SignInFailureCode, string> = {
  INVALID_CREDENTIALS: 'Incorrect username or password.',
  INVALID_CODE: 'That code could not be verified. Please try again.',
  CHALLENGE_EXPIRED: 'Your sign-in session expired. Please sign in again.',
  NOT_CONFIRMED: 'Please confirm your email address before signing in.',
  INVALID_PASSWORD:
    'That password does not meet the requirements: at least 12 characters with uppercase, lowercase, numbers, and special characters.',
  TOO_MANY_ATTEMPTS:
    'Your account has been temporarily locked due to too many failed login attempts. ' +
    'Please try again later or use the "Forgot Password" option to reset your password.',
  UNSUPPORTED_CHALLENGE: 'This account needs a sign-in step the app does not support yet.',
  UNAVAILABLE: 'Sign-in is temporarily unavailable. Please try again in a moment.',
};

/** The first render waits on GET /auth/session; past this the page renders signed out. */
export const SESSION_REQUEST_TIMEOUT_MS = 8_000;

/**
 * Sign-in and challenge answers wait longer: the user is watching a spinner, and a cold API takes
 * 4-9 s before Cognito is even called. Giving up early showed "temporarily unavailable" for a
 * sign-in the API then completed. API Gateway itself stops at 29 s.
 */
export const SIGN_IN_TIMEOUT_MS = 30_000;

// No session interceptors: a 401 here is an answer (signed out), not a reason to redirect.
// Created on first use, so importing this module makes no client.
let authClient: AxiosInstance | null = null;

const authApi = (): AxiosInstance => {
  // HA-447: the cookie in a support view-as tab is the admin's own session. That tab never signs
  // in, refreshes or signs out with it (signing in would end the admin's session).
  if (isViewAs()) {
    throw new Error('Not available in a support session');
  }

  return (authClient ??= axios.create({
    baseURL: `${healioSpaceAPIURL}/api/v1/auth`,
    withCredentials: true,
    timeout: SESSION_REQUEST_TIMEOUT_MS,
    headers: { [HEALIO_REQUEST_HEADER]: '1' },
  }));
};

let current: ServerSession | null = null;

/**
 * A deliberate sign-out is under way. The API ends the session before it answers the logout (it
 * revokes the refresh token first), so the page's other calls in flight come back 401 meanwhile.
 * Those must not be read as an expired session: the sign-out sends the user to the sign-in page
 * itself, with its own message.
 */
let signingOut = false;

export const beginSignOut = (): void => {
  signingOut = true;
};

/** The sign-out didn't complete (the page stays signed in), or it ended. */
export const endSignOut = (): void => {
  signingOut = false;
};

export const isSigningOut = (): boolean => signingOut;

/**
 * HA-457: tabs tell each other when the user signs in or out, so a tab never keeps showing a user
 * whose session has ended or been replaced (the cookie is shared; the page's copy isn't).
 */
type SessionMessage = { type: 'signed-in'; sub?: string } | { type: 'signed-out' };

let channel: BroadcastChannel | null | undefined;

const sessionChannel = (): BroadcastChannel | null => {
  if (channel === undefined) {
    channel =
      typeof BroadcastChannel === 'function' ? new BroadcastChannel('healio-session') : null;
  }

  return channel;
};

const announce = (message: SessionMessage): void => {
  try {
    sessionChannel()?.postMessage(message);
  } catch {
    // Another tab finds out on its next request instead.
  }
};

/**
 * Follows sign-in and sign-out in other tabs: signed out elsewhere goes to the sign-in page; a
 * different user signed in elsewhere reloads, so the page shows who the cookie now belongs to.
 * Not in a support view-as tab, which has its own session (HA-447).
 */
export const followSessionInOtherTabs = (): void => {
  const tabs = isViewAs() ? null : sessionChannel();

  if (!tabs) {
    return;
  }
  tabs.onmessage = (event: MessageEvent<SessionMessage>) => {
    const message = event.data;

    if (message?.type === 'signed-out' && current) {
      current = null;
      window.location.replace('/login?reason=signed_out');
    } else if (message?.type === 'signed-in' && message.sub !== current?.claims.sub) {
      window.location.reload();
    }
  };
};

// The cookie now belongs to someone else (signed in elsewhere): start over as them.
const reloadIfAnotherUser = (session: ServerSession): void => {
  const previous = current?.claims.sub;

  if (previous && session.claims.sub !== previous) {
    window.location.reload();
  }
};

const toSession = (dto: SessionDto, refreshed: boolean): ServerSession => ({
  claims: dto.claims as Identity,
  idleExpiresAt: Date.parse(dto.idleExpiresAt),
  absoluteExpiresAt: Date.parse(dto.absoluteExpiresAt),
  refreshedAt: refreshed ? Date.now() : (current?.refreshedAt ?? Date.now()),
});

/** The signed-in user's session as last read from the API; null when signed out. */
export const getServerSession = (): ServerSession | null => current;

/** When the session ends (the earlier of its idle and absolute limits), ms since epoch. */
export const getServerSessionExpiresAt = (): number | null =>
  current ? Math.min(current.idleExpiresAt, current.absoluteExpiresAt) : null;

/** Forgets the session in this page. The cookie itself is the API's to clear (signOut). */
export const clearServerSession = (): void => {
  current = null;
};

/**
 * Reads the session from the API (GET /auth/session). Doesn't count as activity. Resolves to the
 * session, or null when there is none (401). Any other failure keeps what the page already knew:
 * a page that hasn't read a session yet stays signed out, and a network blip doesn't end one.
 */
export const loadServerSession = async (): Promise<ServerSession | null> => {
  try {
    const response = await authApi().get<SessionDto>('/session');
    const session = toSession(response.data, false);

    reloadIfAnotherUser(session);
    current = session;
  } catch (error) {
    if (isAxiosError(error) && error.response?.status === 401) {
      current = null;
    } else {
      console.warn('[session] Could not read the session:', error);
    }
  }

  return current;
};

/** Tries at page load before giving up and rendering signed out. */
export const SESSION_BOOT_ATTEMPTS = 3;

/**
 * Reads the session at page load. A cold API can take longer than the request timeout to answer;
 * that is retried (the first request warms the instance, so the next is quick) rather than read
 * as signed out. Only a 401 means signed out.
 */
export const bootstrapServerSession = async (
  attempts = SESSION_BOOT_ATTEMPTS
): Promise<ServerSession | null> => {
  for (let attempt = 1; ; attempt++) {
    try {
      const response = await authApi().get<SessionDto>('/session');

      current = toSession(response.data, false);

      return current;
    } catch (error) {
      if (isAxiosError(error) && error.response?.status === 401) {
        current = null;

        return null;
      }
      if (attempt >= attempts) {
        console.warn('[session] Could not read the session at page load:', error);

        return current;
      }
    }
  }
};

/**
 * Tells the API the user is still here (POST /auth/refresh), which extends the idle limit but
 * never the absolute one. `reissue` also gets fresh tokens from Cognito, e.g. after a licence
 * change. False when the session has ended.
 */
export const refreshServerSession = async (reissue = false): Promise<boolean> => {
  try {
    const response = await authApi().post<SessionDto>('/refresh', { reissue });
    const session = toSession(response.data, true);

    reloadIfAnotherUser(session);
    current = session;

    return true;
  } catch (error) {
    if (isAxiosError(error) && error.response?.status === 401) {
      current = null;
    } else {
      console.warn('[session] Refresh failed:', error);
    }

    return false;
  }
};

const failureOf = (error: unknown): SignInError => {
  if (isAxiosError(error)) {
    const code = (error.response?.data as { code?: SignInFailureCode } | undefined)?.code;

    if (code && code in SIGN_IN_MESSAGES) {
      return new SignInError(code, SIGN_IN_MESSAGES[code]);
    }
    // The Auth rate limit (10 a minute per address) answers 429 without a code.
    if (error.response?.status === 429) {
      return new SignInError(
        'TOO_MANY_ATTEMPTS',
        'Too many sign-in attempts. Please wait a minute and try again.'
      );
    }
  }

  return new SignInError('UNAVAILABLE', SIGN_IN_MESSAGES.UNAVAILABLE);
};

interface SignInResponse {
  status: 'signed-in' | 'challenge';
  session?: SessionDto;
  challengeId?: string;
  challengeName?: string;
}

const toStep = (data: SignInResponse): SignInStep => {
  if (data.status === 'challenge' && data.challengeId && data.challengeName) {
    return {
      status: 'challenge',
      challengeId: data.challengeId,
      challengeName: data.challengeName,
    };
  }
  if (data.session) {
    // A new user may be signing in on this browser: nothing cached for the last one carries over.
    clearUserData();
    current = toSession(data.session, true);
    announce({ type: 'signed-in', sub: current.claims.sub });
  }

  return { status: 'signed-in' };
};

/** The password step (POST /auth/login). Throws SignInError when refused. */
export const postSignIn = async (email: string, password: string): Promise<SignInStep> => {
  try {
    return toStep(
      (
        await authApi().post<SignInResponse>(
          '/login',
          { email, password },
          { timeout: SIGN_IN_TIMEOUT_MS }
        )
      ).data
    );
  } catch (error) {
    throw failureOf(error);
  }
};

/**
 * Answers a sign-in challenge (POST /auth/challenge): an MFA code, or a new password. A challenge
 * takes one answer, right or wrong. Throws SignInError when refused.
 */
export const postChallengeAnswer = async (
  challengeId: string,
  answer: string
): Promise<SignInStep> => {
  try {
    return toStep(
      (
        await authApi().post<SignInResponse>(
          '/challenge',
          { challengeId, answer },
          { timeout: SIGN_IN_TIMEOUT_MS }
        )
      ).data
    );
  } catch (error) {
    throw failureOf(error);
  }
};

const PASSWORD_MESSAGES = {
  INVALID_CREDENTIALS: 'Your current password is incorrect.',
  INVALID_PASSWORD: SIGN_IN_MESSAGES.INVALID_PASSWORD,
  INVALID_CODE: 'That code is wrong or has run out. Check it, or request a new code.',
  TOO_MANY_ATTEMPTS: 'Too many attempts. Please wait a few minutes and try again.',
  NO_SESSION: 'Your session has ended. Please sign in again.',
  UNAVAILABLE: 'Something went wrong. Please try again in a moment.',
} as const;

// The API's fixed code as a readable message; never the API's or Cognito's own text.
const passwordFailureOf = (error: unknown): Error => {
  if (isAxiosError(error)) {
    const code = (error.response?.data as { code?: string } | undefined)?.code;

    if (code && code in PASSWORD_MESSAGES) {
      return new Error(PASSWORD_MESSAGES[code as keyof typeof PASSWORD_MESSAGES]);
    }
    // The Auth rate limit answers 429 without a code.
    if (error.response?.status === 429) {
      return new Error(PASSWORD_MESSAGES.TOO_MANY_ATTEMPTS);
    }
  }

  return new Error(PASSWORD_MESSAGES.UNAVAILABLE);
};

// The session has ended on the server: forget it here and tell the other tabs.
const forgetSession = (): void => {
  if (current) {
    current = null;
    announce({ type: 'signed-out' });
  }
};

/**
 * HA-456: changes the signed-in user's password (POST /auth/password). The API ends the user's
 * other sessions and signs them out of Cognito everywhere. 'kept': this session carries on.
 * 'signed-out': it ended too (keeping it needed MFA), so the user signs in again with the new
 * password. Throws an Error with a readable message when the change was refused.
 */
export const postChangePassword = async (
  currentPassword: string,
  newPassword: string
): Promise<'kept' | 'signed-out'> => {
  try {
    const response = await authApi().post<{ status?: string } | ''>(
      '/password',
      { currentPassword, newPassword },
      { timeout: SIGN_IN_TIMEOUT_MS }
    );

    if (response.data && response.data.status === 'signed-out') {
      forgetSession();

      return 'signed-out';
    }

    return 'kept';
  } catch (error) {
    if (isAxiosError(error) && error.response?.status === 401) {
      forgetSession();
    }
    throw passwordFailureOf(error);
  }
};

/**
 * HA-456: emails a password reset code (POST /auth/password/forgot). The API answers the same
 * whether or not the account exists; this throws only when the user should wait or try again.
 */
export const postForgotPassword = async (email: string): Promise<void> => {
  try {
    await authApi().post('/password/forgot', { email }, { timeout: SIGN_IN_TIMEOUT_MS });
  } catch (error) {
    throw passwordFailureOf(error);
  }
};

/**
 * HA-456: sets a new password with the emailed code (POST /auth/password/reset). The API ends every
 * session of the user, this browser's included, so the page forgets its own and tells other tabs.
 */
export const postResetPassword = async (
  email: string,
  code: string,
  newPassword: string
): Promise<void> => {
  try {
    await authApi().post(
      '/password/reset',
      { email, code, newPassword },
      { timeout: SIGN_IN_TIMEOUT_MS }
    );
  } catch (error) {
    throw passwordFailureOf(error);
  }
  forgetSession();
};

/**
 * Ends the session (POST /auth/logout): the API revokes it and clears the cookie. False when the
 * API couldn't be reached, so the session may still be live; the page forgets it either way.
 */
export const postSignOut = async (): Promise<boolean> => {
  try {
    await authApi().post('/logout');
    announce({ type: 'signed-out' });

    return true;
  } catch (error) {
    // A 401 means there was no session left to end.
    if (isAxiosError(error) && error.response?.status === 401) {
      announce({ type: 'signed-out' });

      return true;
    }
    console.warn('[session] Sign-out request failed:', error);

    return false;
  } finally {
    current = null;
  }
};

/**
 * Ends the session while the page navigates away (keepalive outlives the navigation), for the
 * session-expired redirect: whatever made the page give up, the session doesn't outlive it.
 * Never in a support view-as tab, whose cookie is the admin's (HA-447).
 */
export const endServerSessionInBackground = (): void => {
  current = null;
  if (isViewAs() || typeof fetch !== 'function') {
    return;
  }
  announce({ type: 'signed-out' });
  fetch(`${healioSpaceAPIURL}/api/v1/auth/logout`, {
    method: 'POST',
    credentials: 'include',
    keepalive: true,
    headers: { [HEALIO_REQUEST_HEADER]: '1' },
  }).catch(() => undefined);
};

/**
 * Data the app caches in localStorage for the signed-in user. Cleared on sign-in and sign-out so
 * the next user of this browser never sees it.
 */
export const USER_DATA_KEYS = [
  'calendarEvents',
  'healio_dashboard_cache',
  'healio:branding:me',
] as const;

export const clearUserData = (): void => {
  USER_DATA_KEYS.forEach((key) => localStorage.removeItem(key));
};

/**
 * The browser-held token cookies the app used before HA-445. Readable by any script on the page;
 * removed wherever they're still found so they don't outlive the switch.
 */
export const LEGACY_TOKEN_COOKIES = ['idToken', 'accessToken', 'refreshToken'] as const;

export const clearLegacyTokens = (): void => {
  localStorage.removeItem('accessToken');
  localStorage.removeItem('refreshToken');
  LEGACY_TOKEN_COOKIES.forEach((name) => {
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=${window.location.hostname};`;
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=.healiospace.com;`;
  });
};
